import { startAfter } from "firebase/firestore";
import { USER_DB_REF } from "../constants/firestore";
import { DEFAULT_TABLE_PAGINATION } from "../constants/pagination";
import { NAME_KEYWORD_PREFIX, SURNAME_KEYWORD_PREFIX } from "../constants/utils";
import { getNameSurnameKeywords, getStringKeywords, ts2Age } from "../helpers/utils";
import { TablePaginationConfig, TablePaginationResponse } from "../types/common";
import { Family, FamilyFormData } from "../types/families";
import { FamilyTableFilter } from "../types/filters";
import { FirestoreQueryConstraint } from "../types/firebase";
import { firestoreBatch, firestoreBuildQuery, firestoreCollectionRead, firestoreDocDelete, firestoreDocRead, firestoreDocRef, firestoreDocUpdate, firestoreElasticSearch, firestoreEndAt, firestoreEndBefore, firestoreExecuteQuery, firestoreLimit, firestoreLimitToLast, firestoreNewDocRef, firestoreOrderBy, firestoreStartAfter, firestoreStartAt, firestoreWhere, firestoreWrite } from "./common/firebase";
import { getMaxPageNumber, getNextTablePage } from "./tablesPagination";
import { DEFAULT_FAMILY_TABLE_FILTER } from "../constants/flters";
import { doesCaregiverExists } from "./caregiovers";

/* TABLE FILTERS AND PAGINATION */
// FIXME: Refactor [NOMENCLATURA] => inizializzazione tabella famiglie
export async function initFamiliesTable(
  uid: string,
): Promise<TablePaginationResponse<Family>> {
  const { maxPage, size } = DEFAULT_TABLE_PAGINATION;
  const firstPageConfig: TablePaginationConfig = { page: 1, size, maxPage };
  const page = await loadFamiliesNextTablePage(uid, firstPageConfig, DEFAULT_FAMILY_TABLE_FILTER);
  return page;
} 

// TODO: gestione dei filtri
export async function loadFamiliesNextTablePage(
  uid: string,
  paginationConfig: TablePaginationConfig,
  filters: FamilyTableFilter
): Promise<TablePaginationResponse<Family>> {
  const res: Family[] = [];
  const { size, lastDoc } = paginationConfig;
  const queryConstraints: FirestoreQueryConstraint[] = [];
  // filtri
  const { name, surname, age, noCaregiver, withCaregiver, coverage } = filters;
  let name_surname_filter = name ? name.toLocaleLowerCase() : '';
  if (surname) name_surname_filter += `/${surname.toLocaleLowerCase()}`;
  if (name_surname_filter.length > 0) {
    queryConstraints.push(firestoreWhere('metadata.keywords', 'array-contains', name_surname_filter))
  }
  if (age) {
    const ageFilter = parseInt(age);
    if (Number.isInteger(ageFilter)) queryConstraints.push(firestoreWhere('metadata.age', '==', ageFilter));
  }
  if (noCaregiver !== withCaregiver) { // quando hanno entrambi lo stesso valore non hanno effetto sui filtri
    queryConstraints.push(firestoreWhere('assignedCaregiver', noCaregiver ? '==' : '!=', null));
  }
  if (coverage) {
    queryConstraints.push(firestoreWhere('personalData.coverage', '==', coverage));
  }
  // paginazione
  queryConstraints.push(firestoreOrderBy('tsCreated', "desc"));
  if (lastDoc) queryConstraints.push(firestoreStartAfter(lastDoc));
  queryConstraints.push(firestoreLimit(size));
  // esecuzione
  const query = firestoreBuildQuery(`${USER_DB_REF}/${uid}/families`, ...queryConstraints);
  const querySnap = await firestoreExecuteQuery(query);
  querySnap.forEach(el => res.push(el.data() as Family));
  return {
    data: res,
    cursors: {
      first: querySnap.docs[0],
      second: querySnap.docs[1],
      last: querySnap.docs[querySnap.docs.length - 1]
    }
  }
}

export async function loadFamiliesPreviousTablePage(
  uid: string,
  currentPageConfig: TablePaginationConfig,
  filters: FamilyTableFilter
): Promise<TablePaginationResponse<Family>> {
  const res: Family[] = [];
  const { size, firstDoc } = currentPageConfig;
  const queryConstraints: FirestoreQueryConstraint[] = [];
  // filtri
  const { name, surname, age, noCaregiver, withCaregiver, coverage } = filters;
  let name_surname_filter = name ? name.toLocaleLowerCase() : '';
  if (surname) name_surname_filter += `/${surname.toLocaleLowerCase()}`;
  if (name_surname_filter.length > 0) {
    queryConstraints.push(firestoreWhere('metadata.keywords', 'array-contains', name_surname_filter))
  }
  if (age) {
    const ageFilter = parseInt(age);
    if (Number.isInteger(ageFilter)) queryConstraints.push(firestoreWhere('metadata.age', '==', ageFilter));
  }
  if (noCaregiver !== withCaregiver) { // quando hanno entrambi lo stesso valore non hanno effetto sui filtri
    queryConstraints.push(firestoreWhere('assignedCaregiver', noCaregiver ? '==' : '!=', null));
  }
  if (coverage) {
    queryConstraints.push(firestoreWhere('personalData.coverage', '==', coverage));
  }
  // paginazione
  queryConstraints.push(firestoreOrderBy('tsCreated', 'desc'));
  queryConstraints.push(firestoreEndBefore(firstDoc));
  queryConstraints.push(firestoreLimitToLast(size));
  const query = firestoreBuildQuery(`${USER_DB_REF}/${uid}/families`, ...queryConstraints);
  const querySnap = await firestoreExecuteQuery(query);
  querySnap.forEach(el => res.push(el.data() as Family));
  return {
    data: res,
    cursors: {
      first: querySnap.docs[0],
      second: querySnap.docs[1],
      last: querySnap.docs[querySnap.docs.length - 1]
    }
  }
}

// TODO: Calcolare il numero massimo di pagine tenendo conto dei filtri
export async function getFamiliesTableMaxPageNumber(
  uid: string,
  pageSize: number,
  filters?: FamilyTableFilter,
): Promise<number> {
  const queryConstraints: FirestoreQueryConstraint[] = [];
  if (filters) {
    const { name, surname, age, noCaregiver, withCaregiver, coverage } = filters;
    let name_surname_filter = name ? name.toLocaleLowerCase() : '';
    if (surname) name_surname_filter += `/${surname.toLocaleLowerCase()}`;
    if (name_surname_filter.length > 0) { // perchè contiene sempre aleno il carattere speciale
      queryConstraints.push(firestoreWhere('metadata.keywords', 'array-contains', name_surname_filter))
    }
    if (age) {
      const ageFilter = parseInt(age);
      if (Number.isInteger(ageFilter)) queryConstraints.push(firestoreWhere('metadata.age', '==', ageFilter));
    }
    if (noCaregiver !== withCaregiver) { // quando hanno entrambi lo stesso valore non hanno effetto sui filtri
      queryConstraints.push(firestoreWhere('assignedCaregiver', noCaregiver ? '==' : '!=', null));
    }
    if (coverage) {
      queryConstraints.push(firestoreWhere('personalData.coverage', '==', coverage));
    }
  }
  return getMaxPageNumber(`${USER_DB_REF}/${uid}/families`, pageSize, queryConstraints.length > 0 ? queryConstraints : undefined);
}

/* COMMON */
export async function getUserFamilies(
  userId: string,
): Promise<Family[]> {
  const familiesRes: Family[] = [];
  const familiesSnap = await firestoreCollectionRead(`${USER_DB_REF}/${userId}/families`);
  if (familiesSnap.empty) return familiesRes;
  familiesSnap.forEach(family => {
    const familyData = family.data() as Family;
    familiesRes.push(familyData);
  });
  return familiesRes;
}

export async function getUserFamily(
  uid: string,
  familyId: string
): Promise<Family> {
  const famSnap = await firestoreDocRead(`${USER_DB_REF}/${uid}/families`, familyId);
  return famSnap.data() as Family;
}

export async function createUserFamily(
  uid: string,
  familyFormData: FamilyFormData
): Promise<Family> {
  const familyDocRef = firestoreNewDocRef(`${USER_DB_REF}/${uid}/families`);
  const { name, surname } = familyFormData.personalData;
  const nameSurnameKeywords = getStringKeywords(name, surname);
  const ageMetadata = ts2Age(familyFormData.personalData.tsDateOfBirth);
  const familyData: Family = { 
    ...familyFormData, 
    id: familyDocRef.id, 
    tsCreated: Date.now(),
    tsUpdated: Date.now(),
    assignedCaregiver: null,
    metadata: {
      keywords: nameSurnameKeywords,
      normalizedSurname: surname.toLowerCase(),
      age: ageMetadata,
    }
  };
  await firestoreWrite(`${USER_DB_REF}/${uid}/families`, familyDocRef.id, familyData);
  return familyData;
}

export async function deleteUserFamily(
  uid: string,
  family: Family
): Promise<void> {
  if (family.assignedCaregiver) {
    const caregiverExists = await doesCaregiverExists(uid, family.assignedCaregiver);
    if (caregiverExists) {
      const familyRef = firestoreDocRef(`${USER_DB_REF}/${uid}/families`, family.id);
      const caregiverRef = firestoreDocRef(`${USER_DB_REF}/${uid}/caregivers`, family.assignedCaregiver);
      const batch = firestoreBatch();
      const caregiverUpdateObj = { assignedTo: null };
      batch.update(caregiverRef, caregiverUpdateObj);
      batch.delete(familyRef);
      await batch.commit();
    } else {
      await firestoreDocDelete(`${USER_DB_REF}/${uid}/families`, family.id);
    }
  } else {
    await firestoreDocDelete(`${USER_DB_REF}/${uid}/families`, family.id);
  }
}

export async function updateUserFamily(
  uid: string,
  family: Family
) {
  await firestoreWrite(`${USER_DB_REF}/${uid}/families`, family.id, family);
}

export async function updateFamilyWarning(
  uid: string,
  familyId: string,
  warningValue: boolean
): Promise<void> {
  const updateObj = { warning: warningValue };
  await firestoreDocUpdate(`${USER_DB_REF}/${uid}/families`, familyId, updateObj);
}

export async function doesFamilyExists(
  uid: string,
  familyId: string
): Promise<boolean> {
  const familySnap = await firestoreDocRead(`${USER_DB_REF}/${uid}/families`, familyId);
  return familySnap.exists();
}

export async function searchFamilyByName(
  uid: string,
  name: string,
): Promise<Family[]> {
  const searchRes: Family[] = [];
  const searchSnap = await firestoreElasticSearch(`${USER_DB_REF}/${uid}/families`, 'personalData.name', name);
  searchSnap.forEach(el => searchRes.push(el.data() as Family));
  return searchRes;
}

export async function searchFamilyByNameAndSurname(
  uid: string,
  nameSurname: {name?: string; surname?: string}
): Promise<Family[]> {
  const res: Family[] = [];
  const { name, surname } = nameSurname;
  const queryConstraints: FirestoreQueryConstraint[] = [];
  let name_surname_filter = name ? name.toLocaleLowerCase() : '';
  if (surname) name_surname_filter += `/${surname.toLocaleLowerCase()}`;
  if (name_surname_filter.length > 0) {
    queryConstraints.push(firestoreWhere('metadata.keywords', 'array-contains', name_surname_filter));
  }
  queryConstraints.push(firestoreOrderBy('tsCreated'));
  queryConstraints.push(firestoreLimit(5));
  const query = firestoreBuildQuery(`${USER_DB_REF}/${uid}/families`, ...queryConstraints);
  const querySnap = await firestoreExecuteQuery(query);
  querySnap.forEach(f => res.push(f.data() as Family));
  return res;
}

export async function filterUserFamilies(
  uid: string,
  filters: FamilyTableFilter
): Promise<Family[]> {
  const res: Family[] = [];
  const queryConstraints: FirestoreQueryConstraint[] = [];
  const { name, surname, age, withCaregiver, noCaregiver } = filters;
  let name_surname_filter = name ? name.toLocaleLowerCase() : '';
  if (surname) name_surname_filter += `/${surname.toLocaleLowerCase()}`;
  /* if (filters.surname) {
    queryConstraints.push(firestoreOrderBy('metadata.normalizedSurname'));
    queryConstraints.push(firestoreStartAt(filters.surname));
    queryConstraints.push(firestoreEndAt(filters.surname + '\uf8ff'));
  } */
  if (name_surname_filter.length > 0) { // perchè contiene sempre aleno il carattere speciale
    queryConstraints.push(firestoreWhere('metadata.keywords', 'array-contains', name_surname_filter))
  }
  const query = firestoreBuildQuery(`${USER_DB_REF}/${uid}/families`, ...queryConstraints);
  const querySnap = await firestoreExecuteQuery(query);
  querySnap.forEach(el => res.push(el.data() as Family));
  return res;
}

export async function initFamiliesSearchResults(
  uid: string
): Promise<Family[]> {
  const res: Family[] = [];
  const queryConstraints: FirestoreQueryConstraint[] = [firestoreOrderBy('tsUpdated', 'desc'), firestoreLimit(5)];
  const query = firestoreBuildQuery(`${USER_DB_REF}/${uid}/families`, ...queryConstraints);
  const querySnap = await firestoreExecuteQuery(query);
  querySnap.forEach(el => res.push(el.data() as Family));
  return res;
}

export async function assignCaregiverToFamily(
  uid: string,
  familyId: string,
  caregiverId: string
): Promise<void> {
  const batch = firestoreBatch();
  const familyDocRef = firestoreDocRef(`${USER_DB_REF}/${uid}/families`, familyId);
  const caregiverDocRef = firestoreDocRef(`${USER_DB_REF}/${uid}/caregivers`, caregiverId);
  const familyUpdateObj = { assignedCaregiver: caregiverId };
  const caregiverUpdateObj = { assignedTo: familyId };
  batch.update(caregiverDocRef, caregiverUpdateObj);
  batch.update(familyDocRef, familyUpdateObj);
  await batch.commit();
}

export async function removeCaregiverFromFamily(
  uid: string,
  familyId: string,
  caregiverId: string,
): Promise<void> {
  const batch = firestoreBatch();
  const familyRef = firestoreDocRef(`${USER_DB_REF}/${uid}/families`, familyId);
  const caregiverRef = firestoreDocRef(`${USER_DB_REF}/${uid}/caregivers`, caregiverId);
  const familyUpdateObj = { assignedCaregiver: null };
  const caregiverUpdateObj = { assignedTo: null };
  batch.update(caregiverRef, caregiverUpdateObj);
  batch.update(familyRef, familyUpdateObj);
  await batch.commit();
}

export async function searchFamiliesByNameSurname(
  uid: string,
  name: string,
  surname: string
): Promise<Family[]> {
  const res: Family[] = [];
  let name_surname_filter = name.length > 0 ? name.toLocaleLowerCase() : '';
  if (surname.length > 0) name_surname_filter += `/${surname.toLocaleLowerCase()}`;
  const queryConstraints: FirestoreQueryConstraint[] = [
    firestoreWhere('metadata.keywords', 'array-contains', name_surname_filter),
    firestoreOrderBy('tsUpdated', 'desc')
  ];
  const query = firestoreBuildQuery(`${USER_DB_REF}/${uid}/families`, ...queryConstraints);
  const querySnap = await firestoreExecuteQuery(query);
  querySnap.forEach(el => res.push(el.data() as Family));
  return res;
}